import type { JSX } from 'react';

import type { ApplicationExchange } from '@/lib/api/quality-types.js';

/**
 * Fixed line preceding the evidence block. Deliberately factual and attributes nothing — the
 * host is rendered per exchange precisely so the reader, not this line, does the attributing.
 * Never softened into "application error" / "your fault". This exact string is the single source
 * of truth; the PDF surface reuses it rather than declaring its own.
 */
export const APP_EXCHANGE_LINE =
  'Failed HTTP responses recorded during this step:';

/** "5 times" for `occurrences > 1`; `null` for 1, 0, or absent (never shown). */
export function occurrencesLabel(occurrences?: number): string | null {
  return occurrences && occurrences > 1 ? `${occurrences} times` : null;
}

/**
 * Renders the fixed evidence line plus one card per failed HTTP exchange — host, method, path
 * and status as distinct elements, the response's own message as quoted text. The host is shown
 * first: with no origin filtering upstream, it is the field that tells the reader whose server
 * actually answered. The message line is omitted entirely when `message` is empty — an HTML
 * error page, a base64 body, or a JSON body without `message`/`error`/`detail` all legitimately
 * extract to "", and an empty quoted line would look like a rendering bug rather than the true
 * absence it is. Renders nothing when `exchanges` is absent or empty (a harness failure carries
 * no exchange).
 */
export function AppExchangeEvidence({
  exchanges,
}: {
  exchanges?: ApplicationExchange[];
}): JSX.Element | null {
  if (!exchanges || exchanges.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1 pl-3.5">
      <p className="text-xs text-muted-foreground/80">{APP_EXCHANGE_LINE}</p>
      {exchanges.map((exchange, index) => {
        const occurrences = occurrencesLabel(exchange.occurrences);
        return (
          <div
            key={`${exchange.host}-${exchange.method}-${exchange.path}-${exchange.status}-${index}`}
            className="rounded-md border border-border bg-muted/30 px-2 py-1 text-xs"
          >
            {/* min-w-0 on the row and the path: a long API path is nowrap under `truncate`, and a
                flex child cannot shrink below its min-content width without it — which is how one
                exchange stretched the whole cases table sideways. */}
            <div className="flex min-w-0 items-center gap-1.5 font-mono">
              <span className="shrink-0 text-muted-foreground">
                {exchange.host}
              </span>
              <span className="shrink-0 font-semibold">{exchange.method}</span>
              <span
                className="min-w-0 truncate text-muted-foreground"
                title={exchange.path}
              >
                {exchange.path}
              </span>
              <span className="ml-auto shrink-0 font-semibold text-foreground">
                {exchange.status}
              </span>
            </div>
            {exchange.message ? (
              <p className="mt-0.5 break-words text-muted-foreground/80">
                &ldquo;{exchange.message}&rdquo;
              </p>
            ) : null}
            {occurrences ? (
              <p className="mt-0.5 text-muted-foreground/60">{occurrences}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
